import React, { useState, useEffect } from 'react';
import { get, post } from '../api';
import AssistantKeys from '../components/AssistantKeys';

export default function Settings() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    get('/api/settings/status').then(setStatus).catch(() => {});
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await post('/api/subscribers/sync', {});
      setSyncResult(result);
    } catch (err) {
      setSyncResult({ error: err.message });
    }
    setSyncing(false);
  };

  const StatusDot = ({ ok }) => (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: ok ? 'var(--green)' : 'var(--red)', marginRight: 8,
    }} />
  );

  return (
    <div style={{ maxWidth: 700 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Settings</h2>

      <AssistantKeys />

      {/* API Status */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>API Connections</div>
        {status?.services ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div><StatusDot ok={status.services.database?.status === 'connected'} /> Database: {status.services.database?.status || 'unknown'}</div>
            <div><StatusDot ok={status.services.postmark?.configured} /> Postmark: {status.services.postmark?.configured ? 'Configured' : 'Missing token'}</div>
            <div><StatusDot ok={status.services.groundhogg?.configured} /> Groundhogg: {status.services.groundhogg?.configured ? 'Configured' : 'Missing credentials'} — {status.services.groundhogg?.url}</div>
            <div><StatusDot ok={status.services.memberpress?.configured} /> MemberPress: {status.services.memberpress?.configured ? 'Configured' : 'Missing key'} — {status.services.memberpress?.url}</div>
            <div><StatusDot ok={status.services.transcriptapi?.configured} /> TranscriptAPI: {status.services.transcriptapi?.configured ? 'Configured' : 'Missing key'}</div>
          </div>
        ) : (
          <div style={{ color: 'var(--muted)' }}>Loading...</div>
        )}
      </div>

      {/* Groundhogg Sync */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Groundhogg Contact Sync</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
          Pull all contacts from Groundhogg and upsert into the subscribers table. Run this once to seed, then the segment recalculation job handles daily updates.
        </div>
        <button onClick={handleSync} disabled={syncing} style={{
          padding: '10px 20px', fontSize: 13, border: 'none', borderRadius: 8, cursor: syncing ? 'wait' : 'pointer',
          background: 'var(--blue)', color: '#fff', fontWeight: 700, opacity: syncing ? 0.6 : 1,
        }}>
          {syncing ? 'Syncing...' : 'Sync Contacts Now'}
        </button>
        {syncResult && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
            {syncResult.error
              ? <span style={{ color: 'var(--red)' }}>Error: {syncResult.error}</span>
              : <span>Synced {syncResult.synced} contacts — {syncResult.created} new, {syncResult.updated} updated</span>
            }
          </div>
        )}
      </div>

      {/* Config */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Configuration</div>
        {status?.config && (
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div><span style={{ color: 'var(--muted)' }}>Trigger cooldown:</span> {status.config.trigger_cooldown_hours} hours</div>
            <div><span style={{ color: 'var(--muted)' }}>Attribution window:</span> {status.config.attribution_window_hours} hours</div>
            <div><span style={{ color: 'var(--muted)' }}>Test email:</span> {status.config.test_email}</div>
            <div><span style={{ color: 'var(--muted)' }}>Version:</span> {status.version}</div>
          </div>
        )}
      </div>

      {/* Webhook URLs reminder */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Webhook URLs</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
          After deploying to Railway, register these webhook URLs in the respective dashboards:
        </div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>
            <span style={{ color: 'var(--muted)' }}>Postmark:</span>{' '}
            <code style={{ color: 'var(--blue)' }}>{window.location.origin}/webhooks/postmark</code>
          </div>
          <div>
            <span style={{ color: 'var(--muted)' }}>MemberPress:</span>{' '}
            <code style={{ color: 'var(--blue)' }}>{window.location.origin}/webhooks/memberpress</code>
          </div>
        </div>
      </div>
    </div>
  );
}
